import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { withTranslation } from "react-i18next";
import * as moment from "moment";
import { encodeId } from "utils";
import constants from "../../../../../../../constants.js";
import HolidayImage from "assets/images/early_on.svg";
import Loader from "components/Loader";
import toast from "react-hot-toast";
import Empty from "components/Empty";
import { filterEventsBySearch } from "accountOwner/pages/Scheduler/scheduler.function";
import {
  renderOfficeNameOrEnterprise,
  setterCustomSchduler,
  setterPublishOffices,
  matchedBothPatternValues,
  matchedBothDatesValues,
} from "accountOwner/pages/AddEvent/addEvents.functions";
import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import EditOptionsModal, {
  editTypesVariables,
} from "accountOwner/pages/EditEvent/EditOptionsModal";
import styles from "../../../../OfficeSchedulerTab/SubTabs/StaffAvailabilityTab/StaffAvailabilityTab.module.scss";
import schedulerStyles from "../../../../Scheduler.module.scss";
import { useHistory, useLocation } from "react-router-dom";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  updateBusySlot,
  useCreateUpdateSchedulerEventMutation,
  getEventListDetails,
  getBusySlot,
} from "repositories/scheduler-repository";
import { useQueryClient } from "react-query";
import { getOfficeDetail } from "repositories/office-repository";
import MoveToApp from "accountOwner/pages/Scheduler/components/Modals/MoveToApp";
import { ThreeDots } from "react-bootstrap-icons";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import SelectOfficeModal from "accountOwner/pages/Scheduler/components/Modals/SelectOfficeModal";
import { showTime } from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/EventsShiftsTab";
import { formatBusySlotTimeValue } from "./busySlotTime.helpers";

function DailyListView({
  t,
  currentDate,
  selectedOwnerId,
  currentUserId,
  viewSettingFilter = [],
  searchValue = "",
  isFreeStotLoading = false,
  canShowOfficeName = true,
  agenda,
  isLoading,
  isFetching,
  error,
  setDateRange,
  isStaff,
}) {
  const [agendaFilter, setAgendaFilter] = useState([]);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPreviewPosition, setDragPreviewPosition] = useState(null);
  const [editModalConfirmation, setEditModalConfirmation] = useState(false);
  const [pendingDragUpdate, setPendingDragUpdate] = useState(null);
  const [isDragDropLoading, setIsDragDropLoading] = useState(false);
  const [isModal, setIsModal] = useState({ open: false });
  const [showOfficeSelectModal, setShowOfficeSelectModal] = useState(false);
  const [SelectedDateForLeave, setSelectedDateForLeave] = useState(null);
  const history = useHistory();
  const { profile, isOwner, dataAccessValues, effectiveOwnerIdString } =
    useAccountOwnerCommon();
  const createSchedulerEventMutation = useCreateUpdateSchedulerEventMutation();
  const queryClient = useQueryClient();

  const canEditAndViewOthersData = useCallback(
    (officeId) => {
      if (isStaff) {
        return (
          dataAccessValues?.officeList?.some(
            (res) => res.officeId === officeId && res.isAdmin,
          ) || false
        );
      }
      return isOwner;
    },
    [isStaff, isOwner, dataAccessValues?.officeList],
  );

  const isPastDate = (date) => {
    if (!date) return false;
    return moment(date, "YYYY-MM-DDTHH:mm:ss").isBefore(moment(), "day");
  };

  const highlightRef = useRef(null);
  const highlightItemRef = useRef(null);
  const highlightHourRef = useRef(null);
  const calendarWrapperRef = useRef(null);
  const { search } = useLocation();

  // Get date from URL search params for highlighting
  const dateMatchForHighlight = useMemo(() => {
    const searchValue = new URLSearchParams(search).get(showTime);
    if (searchValue?.includes("NaN") || !search) {
      return null;
    }
    return new Date(+searchValue);
  }, [search]);

  // Set date range for single day
  useEffect(() => {
    if (currentDate && setDateRange) {
      const range = {
        startDate: moment(currentDate).format("YYYY-MM-DD"),
        endDate: moment(currentDate).format("YYYY-MM-DD"),
      };
      setDateRange(range);
    }
  }, [currentDate, setDateRange]);

  // Process agenda data - agenda is already filtered by parent and grouped by date
  useEffect(() => {
    if (!isLoading) {
      if (agenda && Array.isArray(agenda)) {
        setAgendaFilter(agenda);
      } else {
        setAgendaFilter([]);
      }
    }
  }, [isLoading, agenda]);

  useEffect(() => {
    if (!isLoading && !isFetching && error?.message) {
      toast.error(error.message);
    }
  }, [error, isLoading, isFetching]);

  // Transform agenda data for single day view
  const dailyAgendaData = useMemo(() => {
    if (!agendaFilter?.length || !currentDate) return null;

    const currentDateMoment = moment(currentDate).startOf("day");
    const dateObj = agendaFilter.find((dateItem) => {
      if (!dateItem.requestedDate) return false;
      const dateMoment = moment(dateItem.requestedDate).startOf("day");
      return dateMoment.isSame(currentDateMoment, "day");
    });

    if (!dateObj) return null;

    const {
      requestedDate,
      appointments = [],
      leaves = [],
      schedulerBusySlots = [],
      schedulerEvents = [],
      appointmentSlots = [],
      holidayList = [],
    } = dateObj;

    return {
      requestedDate,
      holidayList,
      // Create a single "user" entry since it's My Schedule (current user only)
      officeSchedulerList: [
        {
          user: {
            id: currentUserId,
            fullName: profile?.fullName || "",
            profilePic: profile?.profilePic || null,
            officeNames: [],
          },
          schedulerEvents: Array.isArray(schedulerEvents)
            ? schedulerEvents
            : [],
          schedulerBusySlots: Array.isArray(schedulerBusySlots)
            ? schedulerBusySlots
            : [],
          appointments: Array.isArray(appointments) ? appointments : [],
          leaves: Array.isArray(leaves) ? leaves : [],
          appointmentSlots: Array.isArray(appointmentSlots)
            ? appointmentSlots
            : [],
        },
      ],
    };
  }, [agendaFilter, currentDate, currentUserId, profile]);

  // Generate hours (12 AM to 11 PM - 24 hours)
  const hours = useMemo(() => {
    const hourList = [];
    for (let i = 0; i <= 23; i++) {
      const hour = moment().hour(i).minute(0);
      hourList.push({
        label: hour.format("h A"),
        moment: hour,
        hour: i,
      });
    }
    return hourList;
  }, []);

  // Helper function to get all events from agenda item
  const getAllEvents = useCallback((scheduler) => {
    const events = [];

    // Add scheduler events
    if (scheduler.schedulerEvents) {
      events.push(
        ...scheduler.schedulerEvents.map((event) => ({
          ...event,
          type: "schedulerEvent",
        })),
      );
    }

    // Add appointment slots
    if (scheduler.appointmentSlots) {
      events.push(
        ...scheduler.appointmentSlots.map((slot) => ({
          ...slot,
          type: "appointmentSlot",
          title: slot.reasonForBlock || "Appointment Slot",
        })),
      );
    }

    // Add busy slots
    if (scheduler.schedulerBusySlots) {
      events.push(
        ...scheduler.schedulerBusySlots.map((slot) => ({
          ...slot,
          type: "busySlot",
          isBusySlot: true,
          agendaType: slot.agendaType || constants.agendaType.BUSY_SLOT,
          title: slot.title || "Busy",
        })),
      );
    }

    // Add appointments
    if (scheduler.appointments) {
      events.push(
        ...scheduler.appointments.map((appointment) => ({
          ...appointment,
          type: "appointment",
          title: appointment.description || "Appointment",
        })),
      );
    }

    // Add leaves
    if (scheduler.leaves) {
      events.push(
        ...scheduler.leaves.map((leave) => ({
          ...leave,
          type: "leave",
          isAllDayEvent: true,
          title: leave.reason || "Leave",
        })),
      );
    }

    return events;
  }, []);

  // Categorize events into all-day and time-based
  const categorizeEvents = useCallback((events) => {
    return {
      allDayEvents: events.filter(
        (event) =>
          event.isAllDayEvent ||
          event.isAllDayBusy ||
          (event.startTime &&
            event.endTime &&
            new Date(event.startTime).toDateString() !==
              new Date(event.endTime).toDateString()),
      ),
      timeEvents: events.filter(
        (event) =>
          !event.isAllDayEvent &&
          !event.isAllDayBusy &&
          event.startTime &&
          event.endTime &&
          new Date(event.startTime).toDateString() ===
            new Date(event.endTime).toDateString(),
      ),
    };
  }, []);

  // Get event colors based on type (matching AgendaWeeklyView logic)
  const getEventColor = useCallback(
    (event) => {
      if (!event || !event.agendaType) {
        return "#c7e9ff";
      }
      const pendingCheck =
        event?.statusId === constants.agendaType.PENDINGLEAVE;

      switch (event.agendaType) {
        case constants.agendaType.LEAVE:
          return pendingCheck ? "#fdeded" : "rgba(113, 213, 185, 0.2)";
        case constants.agendaType.BUSY_SLOT:
        case constants.agendaType.BLOCKED:
          return "#fef3d7";
        case constants.agendaType.EVENT: {
          const createdByMe = currentUserId === event.createdById;
          const status =
            event.eventEmployeeStatus || event.eventRequestToJoinStatus;
          if (createdByMe) {
            return "#f9fcee";
          } else if (status === constants.SCHEDULERSTATUS.PENDING) {
            return "#e1f4fd";
          } else {
            return "#f2f3ef";
          }
        }
        case constants.agendaType.APPOINTMENT:
          return "#f6f1fd";
        default:
          return "#c7e9ff";
      }
    },
    [currentUserId],
  );

  const getEventBorderColor = useCallback(
    (event) => {
      if (!event || !event.agendaType) {
        return "#005f99";
      }
      const pendingCheck =
        event?.statusId === constants.agendaType.PENDINGLEAVE;
      switch (event.agendaType) {
        case constants.agendaType.LEAVE:
          return pendingCheck ? "#ff5757" : "#71d5b9";
        case constants.agendaType.BUSY_SLOT:
        case constants.agendaType.BLOCKED:
          return "#f7cb7a";
        case constants.agendaType.EVENT:
          if (event.eventStatus === constants.SCHEDULERSTATUS.PENDING) {
            return "#82d8f8";
          } else if (currentUserId === event.createdById) {
            return "#f7cb7a";
          } else {
            return "#707070";
          }
        case constants.agendaType.APPOINTMENT:
          return "#d8b4f8";
        default:
          return "#005f99";
      }
    },
    [currentUserId],
  );

  // Process events from daily agenda data
  const { allDayEvents, timeEvents } = useMemo(() => {
    if (!dailyAgendaData?.officeSchedulerList?.length) {
      return { allDayEvents: [], timeEvents: [] };
    }

    const scheduler = dailyAgendaData.officeSchedulerList[0];
    const allEvents = getAllEvents(scheduler);
    const categorized = categorizeEvents(allEvents);

    // Process all-day events
    const processedAllDayEvents = categorized.allDayEvents.map((item) => {
      const bgColor = getEventColor(item);
      const dotColor = getEventBorderColor(item);
      return {
        bgColor,
        dotColor,
        item,
        user: scheduler.user,
      };
    });

    // Process time-based events
    const processedTimeEvents = categorized.timeEvents
      .filter((item) => item.startTime && item.endTime)
      .map((item) => {
        const startMoment = moment(item.startTime);
        const endMoment = moment(item.endTime);
        const requestedMoment = moment(dailyAgendaData.requestedDate);

        startMoment.year(requestedMoment.year());
        startMoment.month(requestedMoment.month());
        startMoment.date(requestedMoment.date());

        endMoment.year(requestedMoment.year());
        endMoment.month(requestedMoment.month());
        endMoment.date(requestedMoment.date());

        const bgColor = getEventColor(item);
        const dotColor = getEventBorderColor(item);

        return {
          bgColor,
          dotColor,
          item: {
            ...item,
            startTime: startMoment.toISOString(),
            endTime: endMoment.toISOString(),
          },
          user: scheduler.user,
        };
      });

    return {
      allDayEvents: processedAllDayEvents,
      timeEvents: processedTimeEvents,
    };
  }, [
    dailyAgendaData,
    getAllEvents,
    categorizeEvents,
    getEventColor,
    getEventBorderColor,
  ]);

  // Get event display text
  const getEventDisplayText = useCallback(
    (event) => {
      if (!event) return null;
      let eventTitle = event?.title || "";

      // Override title for Leave and Appointment
      if (event?.agendaType === constants.agendaType.LEAVE) {
        eventTitle = "On Leave";
      } else if (event?.agendaType === constants.agendaType.APPOINTMENT) {
        eventTitle = "Patient Appointment";
      }

      const officeName = renderOfficeNameOrEnterprise(event, t);

      return (
        <div className="d-flex flex-column">
          {(!viewSettingFilter.length ||
            viewSettingFilter.includes("title")) && (
            <div className={styles["event-office"]}>{eventTitle}</div>
          )}
          {canShowOfficeName &&
            viewSettingFilter.length > 0 &&
            viewSettingFilter.includes("officeName") && (
              <div className={styles["event-office"]}>{officeName}</div>
            )}
        </div>
      );
    },
    [t, viewSettingFilter, canShowOfficeName],
  );

  // Helper functions for time-based event positioning
  const getTimeInMinutes = useCallback((time) => {
    if (!time) {
      return 0;
    }
    const date = new Date(time);
    if (isNaN(date.getTime())) {
      return 0;
    }
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return hours * 60 + minutes;
  }, []);

  // Helper function to get time range from hours array (view's time range)
  const getTimeRange = useCallback(() => {
    const minHour = hours.length > 0 ? hours[0].hour : 0;
    const maxHour = hours.length > 0 ? hours[hours.length - 1].hour : 23;
    return {
      startTime: moment().hour(minHour).minute(0).second(0),
      endTime: moment().hour(maxHour).minute(59).second(59),
    };
  }, [hours]);

  // Helper function to check if time is within allowed range
  const isTimeWithinRange = useCallback((timeMoment, timeRange, baseDate) => {
    if (!timeMoment || !timeRange) return true;

    if (baseDate && !timeMoment.isSame(baseDate, "day")) {
      return false;
    }

    const timeInMinutes = timeMoment.hour() * 60 + timeMoment.minute();
    const rangeStartMinutes =
      timeRange.startTime.hour() * 60 + timeRange.startTime.minute();
    const rangeEndMinutes =
      timeRange.endTime.hour() * 60 + timeRange.endTime.minute();

    return (
      timeInMinutes >= rangeStartMinutes && timeInMinutes <= rangeEndMinutes
    );
  }, []);

  const calculateOverlapPositions = useCallback(
    (events) => {
      events.sort(
        (a, b) => getTimeInMinutes(a.startTime) - getTimeInMinutes(b.startTime),
      );

      const positions = [];

      for (let i = 0; i < events.length; i++) {
        let overlaps = [events[i]];
        for (let j = i + 1; j < events.length; j++) {
          const endA = getTimeInMinutes(events[i].endTime);
          const startB = getTimeInMinutes(events[j].startTime);

          if (startB < endA) {
            overlaps.push(events[j]);
          }
        }

        const totalOverlap = overlaps.length;
        overlaps.forEach((event, index) => {
          positions.push({
            event,
            widthFactor: 1 / totalOverlap,
            leftOffsetFactor: index / totalOverlap,
          });
        });

        i += overlaps.length - 1;
      }

      return positions;
    },
    [getTimeInMinutes],
  );

  const groupEventsByStartTime = useCallback(
    (events) => {
      const grouped = {};
      events.forEach((event) => {
        const startMinutes = getTimeInMinutes(event.startTime);
        if (!grouped[startMinutes]) {
          grouped[startMinutes] = [];
        }
        grouped[startMinutes].push(event);
      });
      return grouped;
    },
    [getTimeInMinutes],
  );

  const goToDetails = useCallback(
    (id, type, createdByMe, requestedDate, isInvite = false, eventItem) => {
      if (!id || !type) return;
      switch (type) {
        case constants.agendaType.EVENT: {
          let pathname = "/";
          pathname = createdByMe
            ? constants.routes.scheduler.eventDetails.replace(
                ":eventId",
                encodeId(id),
              )
            : isInvite
              ? constants.routes.scheduler.eventRequestDetails.replace(
                  ":eventId",
                  encodeId(id),
                )
              : constants.routes.scheduler.EventWorkingDetails.replace(
                  ":eventId",
                  encodeId(id),
                );
          history.push({
            pathname,
            state: {
              requestedDate,
              fromScreen: "mySchedule",
            },
            search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
          });
          break;
        }
        case constants.agendaType.BUSY_SLOT: {
          const pathname = constants.routes.scheduler.busySlotDetail.replace(
            ":busySlotId",
            encodeId(id),
          );
          history.push({
            pathname,
            state: {
              requestedDate,
              fromScreen: "mySchedule",
            },
            search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
          });
          break;
        }
        case constants.agendaType.LEAVE: {
          const isBeforeToday = moment(eventItem?.startDate).isBefore(
            moment().startOf("day"),
          );
          if (eventItem?.startDate && !isBeforeToday) {
            history.push({
              pathname: constants.routes.staff.editLeaves.replace(
                ":officeId",
                encodeId(eventItem.officeId),
              ),
              state: { ...eventItem, userId: profile.id, backRedirect: true },
              search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
            });
          } else if (isBeforeToday) {
            toast.error("You cannot edit the leaves for past dates");
          }
          break;
        }
        default: {
          return;
        }
      }
    },
    [history,effectiveOwnerIdString,profile?.id],
  );

  // Check if event can be dragged
  const canDragEvent = useCallback(
    (eventItem) => {
      const createdByMe = eventItem.createdById === currentUserId;
      const ownedByMe =
        eventItem.ownerId === selectedOwnerId ||
        eventItem.ownerId === currentUserId;
      const createdByOthers =
        eventItem?.isShift && profile?.id !== eventItem?.createdById;
      const isBusySlot =
        eventItem.agendaType === constants.agendaType.BUSY_SLOT ||
        eventItem.isBusySlot === true;
      const isBlocked = eventItem.agendaType === constants.agendaType.BLOCKED;

      if (isBusySlot || isBlocked) {
        if (isBusySlot) {
          const hasCreator = createdByMe;
          const hasOwner =
            ownedByMe ||
            eventItem.ownerId === selectedOwnerId ||
            eventItem.ownerId === currentUserId;
          const noOwnerInfo = !eventItem.createdById && !eventItem.ownerId;
          return hasCreator || hasOwner || noOwnerInfo;
        }
        return createdByMe;
      }

      if (eventItem?.isShift) {
        if (createdByMe) {
          return true;
        }
        if (createdByOthers && canEditAndViewOthersData(eventItem?.officeId)) {
          return true;
        }
        return false;
      }

      if (eventItem.agendaType === constants.agendaType.EVENT) {
        return createdByMe;
      }

      return false;
    },
    [currentUserId, selectedOwnerId, profile?.id, canEditAndViewOthersData],
  );

  // Calculate new date and time from drop position with 15-minute snap
  const calculateNewDateTime = useCallback(
    (eventItem, dropY, cellElement, hourIndex) => {
      if (!cellElement || !eventItem || hourIndex === undefined) {
        return null;
      }

      const cellRect = cellElement.getBoundingClientRect();
      const relativeY = dropY - cellRect.top;
      const cellHeight = cellRect.height;
      const minuteHeight = cellHeight / 60;
      const minutesFromHourStart = relativeY / minuteHeight;

      const snappedMinutes = Math.round(minutesFromHourStart / 15) * 15;
      const clampedMinute = Math.max(0, Math.min(45, snappedMinutes));

      const newDate = moment(currentDate);
      const newStartTime = moment(newDate)
        .hour(hourIndex)
        .minute(clampedMinute)
        .second(0)
        .millisecond(0);

      const timeRange = getTimeRange();

      if (!eventItem.startTime || !eventItem.endTime) {
        const durationMinutes = 60;
        const newEndTime = moment(newStartTime).add(durationMinutes, "minutes");

        if (
          !isTimeWithinRange(newStartTime, timeRange, newDate) ||
          !isTimeWithinRange(newEndTime, timeRange, newDate)
        ) {
          return { error: "timeRangeExceeded" };
        }

        return {
          newDate: newDate.toDate(),
          newStartTime: newStartTime.toDate(),
          newEndTime: newEndTime.toDate(),
          newStartTimeString: newStartTime.format("YYYY-MM-DDTHH:mm"),
          newEndTimeString: newEndTime.format("YYYY-MM-DDTHH:mm"),
        };
      }

      const originalStart = moment(eventItem.startTime);
      const originalEnd = moment(eventItem.endTime);
      const durationMinutes = originalEnd.diff(originalStart, "minutes");

      const newEndTime = moment(newStartTime).add(durationMinutes, "minutes");

      if (
        !isTimeWithinRange(newStartTime, timeRange, newDate) ||
        !isTimeWithinRange(newEndTime, timeRange, newDate)
      ) {
        return { error: "timeRangeExceeded" };
      }

      return {
        newDate: newDate.toDate(),
        newStartTime: newStartTime.toDate(),
        newEndTime: newEndTime.toDate(),
        newStartTimeString: newStartTime.format("YYYY-MM-DDTHH:mm"),
        newEndTimeString: newEndTime.format("YYYY-MM-DDTHH:mm"),
      };
    },
    [currentDate, getTimeRange, isTimeWithinRange],
  );

  // Helper to check if time changed
  const getTimeCheck = useCallback((alreadyTime, selectedTime) => {
    if (alreadyTime) {
      const selectedStartTime = moment(selectedTime);
      const alreadySelectedStartTime = moment(alreadyTime);
      if (
        selectedStartTime.hour() !== alreadySelectedStartTime.hour() ||
        selectedStartTime.minute() !== alreadySelectedStartTime.minute()
      ) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, []);

  // Internal function to perform the actual update
  const performEventUpdate = useCallback(
    async (eventData, newDateTime, editType, originalRequestedDate = null) => {
      if (!newDateTime || !eventData) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      const requestedDateForCheck = originalRequestedDate
        ? originalRequestedDate
        : eventData.requestedDate
          ? moment(eventData.requestedDate).format("YYYY-MM-DD")
          : eventData.startTime
            ? moment(eventData.startTime).format("YYYY-MM-DD")
            : moment(eventData.date || newDateTime.newDate).format(
                "YYYY-MM-DD",
              );

      const dateChangedResult = matchedBothDatesValues({
        eventDetail: eventData,
        repeatedEndDate: eventData.repeatedEndDate
          ? moment(eventData.repeatedEndDate).toDate()
          : moment().add(3, "month").toDate(),
        requestedDate: requestedDateForCheck,
        eventDate: newDateTime.newDate,
      });

      const startTimeChanged = getTimeCheck(
        eventData.startTime,
        newDateTime.newStartTimeString,
      );
      const endTimeChanged = getTimeCheck(
        eventData.endTime,
        newDateTime.newEndTimeString,
      );

      const originalRequestedDateForParams = originalRequestedDate
        ? moment(originalRequestedDate).format("YYYY-MM-DDTHH:mm")
        : eventData.requestedDate
          ? moment(eventData.requestedDate).format("YYYY-MM-DDTHH:mm")
          : eventData.startTime
            ? moment(eventData.startTime).format("YYYY-MM-DDTHH:mm")
            : moment(newDateTime.newDate).format("YYYY-MM-DDTHH:mm");

      const requestedDateForDateCalc = originalRequestedDate
        ? moment(originalRequestedDate).toDate()
        : eventData.requestedDate
          ? moment(eventData.requestedDate).toDate()
          : eventData.startTime
            ? moment(eventData.startTime).toDate()
            : null;

      const eventDate = moment(newDateTime.newDate).toDate();

      const params = {
        SchedulerEventId: eventData.id,
        OwnerId: selectedOwnerId || currentUserId,
        Title: eventData.title,
        Date: moment(
          requestedDateForDateCalc &&
            moment(requestedDateForDateCalc)
              .startOf("day")
              .isSame(moment(eventDate).startOf("day"))
            ? eventData?.date
            : eventDate,
        ).format("YYYY-MM-DDTHH:mm"),
        StartTime: newDateTime.newStartTimeString,
        EndTime: newDateTime.newEndTimeString,
        IsAllDayEvent: eventData.isAllDayEvent || false,
        RepeatedType: eventData.repeatedType || 1,
        RepeatedDay: moment(newDateTime.newDate).isoWeekday(),
        RepeatedEndDate:
          eventData.repeatedType > 1 && eventData.repeatedEndDate
            ? moment(eventData.repeatedEndDate).format("YYYY-MM-DD")
            : null,
        Location: eventData.location || "",
        BlockForPatient: eventData.blockForPatient || false,
        Note: eventData.note || "",
        PublishAsEvent: eventData.publishAsEvent || false,
        eventEmployees: Array.isArray(eventData.eventEmployees)
          ? eventData.eventEmployees
          : [],
        EventRoles: Array.isArray(eventData.eventRoles)
          ? eventData.eventRoles
          : [],
        EventTags: Array.isArray(eventData.eventTags)
          ? eventData.eventTags
          : [],
        CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
        ReminderBeforeInMins: eventData.reminderBeforeInMins || 0,
        isDraft: eventData.isDraft || false,
        requestedDate: originalRequestedDateForParams,
        timezoneCode: eventData.timezoneCode || moment.tz.guess(),
        EditType: editType || editTypesVariables.allEvent,
        UpdateFlags: {
          isTitleEdit: false,
          isStartTimeEdit: startTimeChanged,
          isEndTimeEdit: endTimeChanged,
          isAllDayEventEdit: false,
          isNoteEdit: false,
          isLocationEdit: false,
          isReminderBeforeInMinsEdit: false,
          isEnterpriseEdit: false,
          isOfficeIdEdit: false,
          EmployeeIds: [],
          RoleIds: [],
          IsRepeatTypeChange: false,
          IsEventDateChange: dateChangedResult.startDateChanged,
          IsRepeatedEndDateChange: dateChangedResult.endDateChanged,
          IsBlockForPatientChange: false,
          tagsIds: false,
        },
      };

      if (eventData.isShift) {
        params.Title = eventData.title || "";
        delete params.Location;
        delete params.Note;
        if (
          Array.isArray(eventData.eventEmployees) &&
          eventData.eventEmployees.length > 0 &&
          eventData.eventEmployees[0]
        ) {
          params.officeId = eventData.eventEmployees[0].officeId || null;
        }
      } else {
        params.IsAutoApprove = eventData.isAutoApprove || false;
      }

      try {
        setterCustomSchduler({
          params,
          repeatedType: eventData.repeatedType || 1,
          repeatedTypeFrequency: eventData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: eventData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            eventData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: eventData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            eventData.customRepeatPattern?.monthsOfYear?.map((m) => m?.month) ||
            [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: eventData,
        });
      } catch (error) {
        console.warn("Error in setterCustomSchduler:", error);
      }

      try {
        const preferredOption = {
          dropdown: eventData.publishAsEvent
            ? [{ id: "allowInvitees" }]
            : eventData.isDraft
              ? [{ id: "saveDraft" }]
              : [{ id: "inviteesOnly" }],
          otherOptions: [],
        };
        setterPublishOffices({
          preferredOption,
          userIds: Array.isArray(eventData.eventEmployees)
            ? eventData.eventEmployees
            : [],
          publishAsEvent: eventData.publishAsEvent
            ? "allowInvitees"
            : eventData.isDraft
              ? "saveDraft"
              : "inviteesOnly",
          nonInviteesPublish: eventData.publishedType || "allOffices",
          params,
          officeIdForNonInvitee: Array.isArray(
            eventData.schedulerEventPublishedOffices,
          )
            ? eventData.schedulerEventPublishedOffices.filter(
                (office) => office && office.id,
              )
            : [],
          edit: true,
          isDraft: eventData.isDraft || false,
        });
      } catch (error) {
        console.warn("Error in setterPublishOffices:", error);
      }

      await createSchedulerEventMutation.mutateAsync(params);

      queryClient.invalidateQueries(["agenda"]);

      toast.success(t("staff.eventUpdated"));
    },
    [
      selectedOwnerId,
      currentUserId,
      createSchedulerEventMutation,
      t,
      queryClient,
      getTimeCheck,
    ],
  );

  // Update event/shift via API (with EditEvent-style checks and modal)
  const updateEventAfterDrag = useCallback(
    async (eventItem, newDateTime, editType = null) => {
      if (!newDateTime || !eventItem || !eventItem.id) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      try {
        const originalRequestedDate = moment(
          currentDate || eventItem.startTime || newDateTime.newDate,
        ).format("YYYY-MM-DD");
        const eventDetails = await getEventListDetails(
          eventItem.id,
          originalRequestedDate,
        );
        const eventData = eventDetails?.data;

        if (!eventData) {
          toast.error(t("scheduler.eventNotFound"));
          return;
        }

        const dateChangedResult = matchedBothDatesValues({
          eventDetail: eventData,
          repeatedEndDate: eventData.repeatedEndDate
            ? moment(eventData.repeatedEndDate).toDate()
            : moment().add(3, "month").toDate(),
          requestedDate: originalRequestedDate,
          eventDate: newDateTime.newDate,
        });

        const repeatedOptionChanged = matchedBothPatternValues({
          repeatedType: eventData.repeatedType || 1,
          repeatedTypeFrequency: eventData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: eventData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            eventData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: eventData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            eventData.customRepeatPattern?.monthsOfYear?.map((m) => m?.month) ||
            [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: eventData,
        });

        const anyChangedForDate = repeatedOptionChanged;

        const startTimeChanged = getTimeCheck(
          eventData.startTime,
          newDateTime.newStartTimeString,
        );
        const endTimeChanged = getTimeCheck(
          eventData.endTime,
          newDateTime.newEndTimeString,
        );

        const hasChanges =
          dateChangedResult.startDateChanged ||
          dateChangedResult.endDateChanged ||
          startTimeChanged ||
          endTimeChanged;

        const neverCheck =
          eventData.repeatedType ===
          addEditEventsConst.repeatedOptionsValues.never;
        if (!neverCheck && eventData.repeatedEndDate) {
          const newDateStr = moment(newDateTime.newDate).format("YYYY-MM-DD");
          const repeatedEndDateStr = moment(eventData.repeatedEndDate).format(
            "YYYY-MM-DD",
          );
          if (
            !moment(newDateStr).isBefore(repeatedEndDateStr) &&
            newDateStr !== repeatedEndDateStr
          ) {
            toast.error(t("form.errors.endGreater"));
            return;
          }
        }

        const isNotDrafted = !eventData.isDraft;

        if (!neverCheck && !editType && hasChanges && isNotDrafted) {
          setPendingDragUpdate({
            eventData,
            newDateTime,
            anyChangedForDate,
            originalRequestedDate,
          });
          setEditModalConfirmation(true);
          return;
        }

        let finalEditType = editType;
        if (!finalEditType) {
          if (neverCheck) {
            finalEditType = editTypesVariables.allEvent;
          } else {
            finalEditType = editTypesVariables.allEvent;
          }
        }

        await performEventUpdate(
          eventData,
          newDateTime,
          finalEditType,
          originalRequestedDate,
        );
      } catch (error) {
        toast.error(error.message || t("scheduler.updateFailed"));
        throw error;
      }
    },
    [t, getTimeCheck, performEventUpdate],
  );

  // Internal function to perform the actual busy slot update
  const performBusySlotUpdate = useCallback(
    async (
      busySlotData,
      newDateTime,
      editType,
      officeDetails,
      officeIds,
      busySlotEmployeeView,
      isAllDayBusy,
      repeatedType,
      repeatedEndDate,
      originalRequestedDate,
      UpdateFlags,
    ) => {
      if (!newDateTime || !busySlotData) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      const eventDateMoment = moment(newDateTime.newDate);
      const startTime = moment(newDateTime.newStartTime);
      const endTime = moment(newDateTime.newEndTime);

      const requestedDateForDateCalc = originalRequestedDate
        ? moment(originalRequestedDate).toDate()
        : busySlotData.requestedDate
          ? moment(busySlotData.requestedDate).toDate()
          : busySlotData.startTime
            ? moment(busySlotData.startTime).toDate()
            : null;

      const eventDate = eventDateMoment.toDate();

      const params = {
        OwnerId: officeDetails.ownerId,
        OfficeIds: officeIds,
        Title: busySlotData.title || "",
        Date: moment(
          requestedDateForDateCalc &&
            moment(requestedDateForDateCalc)
              .startOf("day")
              .isSame(moment(eventDate).startOf("day"))
            ? busySlotData?.date
            : eventDate,
        ).format("YYYY-MM-DDTHH:mm"),
        StartTime: formatBusySlotTimeValue({
          eventDateMoment,
          isAllDayBusy,
          timeValue: startTime,
          startOrEnd: "startOf",
        }),
        EndTime: formatBusySlotTimeValue({
          eventDateMoment,
          isAllDayBusy,
          timeValue: endTime,
          startOrEnd: "endOf",
        }),
        repeatedType: +repeatedType,
        CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
        repeatedDay: +repeatedType === 1 ? null : eventDateMoment.isoWeekday(),
        RepeatedEndDate:
          +repeatedType === 1
            ? null
            : moment(repeatedEndDate).format("YYYY-MM-DD"),
        Location: busySlotData.location || "",
        Description: busySlotData.description || "",
        IsNotifyEmployee: busySlotData.isNotifyEmployee || false,
        BusySlotEmployeeView: busySlotEmployeeView,
        IsAllDayBusy: isAllDayBusy,
      };

      try {
        setterCustomSchduler({
          params,
          repeatedType: repeatedType,
          repeatedTypeFrequency: busySlotData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: busySlotData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            busySlotData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: busySlotData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            busySlotData.customRepeatPattern?.monthsOfYear?.map(
              (m) => m?.month,
            ) || [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: busySlotData,
        });
      } catch (error) {
        console.warn("Error in setterCustomSchduler:", error);
      }

      params.UpdateFlags = UpdateFlags;
      params.EditType = editType || 3;
      params.SchedulerBusySlotId = busySlotData.id;
      params.RequestedDate = originalRequestedDate;
      delete params.isRepeated;

      await updateBusySlot(params);

      queryClient.invalidateQueries(["agenda"]);

      toast.success(t("scheduler.busySlotUpdated"));
    },
    [t, queryClient],
  );

  // Update busy slot via API
  const updateBusySlotAfterDrag = useCallback(
    async (eventItem, newDateTime, editType = null) => {
      if (!newDateTime || !eventItem || !eventItem.id) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      try {
        const originalRequestedDate = moment(
          currentDate ||
            eventItem.startTime ||
            eventItem.date ||
            newDateTime.newDate,
        ).format("YYYY-MM-DD");

        let busySlotDetails = null;
        try {
          busySlotDetails = await getBusySlot({
            queryKey: [
              "busy-slot-details",
              eventItem.id,
              originalRequestedDate,
            ],
          });
        } catch (error) {
          toast.error(error.message || t("scheduler.updateFailed"));
          return;
        }

        const busySlotData = busySlotDetails?.data;

        if (!busySlotData) {
          toast.error(t("scheduler.eventNotFound"));
          return;
        }

        const officeId = busySlotData.officeId || eventItem.officeId;
        if (!officeId) {
          toast.error(t("scheduler.officeIdRequired"));
          return;
        }

        let officeDetails = null;
        try {
          officeDetails = await getOfficeDetail(officeId);
        } catch (error) {
          toast.error(
            error.message || t("scheduler.failedToFetchOfficeDetails"),
          );
          return;
        }

        if (!officeDetails) {
          toast.error(t("scheduler.officeDetailsNotFound"));
          return;
        }

        const isAllDayBusy =
          eventItem.isAllDayBusy ||
          eventItem.isAllDayEvent ||
          !eventItem.startTime ||
          !eventItem.endTime;

        const eventDate = moment(newDateTime.newDate);
        const startTime = moment(newDateTime.newStartTime);
        const endTime = moment(newDateTime.newEndTime);

        const repeatedType = +(busySlotData.repeatedType || 1);
        const repeatedEndDate = busySlotData.repeatedEndDate
          ? moment(busySlotData.repeatedEndDate)
          : moment().toDate();

        let officeIds = [];
        if (
          busySlotData.schedulerBusySlotOffice &&
          Array.isArray(busySlotData.schedulerBusySlotOffice)
        ) {
          officeIds = busySlotData.schedulerBusySlotOffice.map((ele) => ({
            id: ele.externalId || ele.id || 0,
            SchedulerBusySlotId: busySlotData.id,
            OfficeId: ele.officeId,
            IsDeleted: false,
          }));
        } else {
          officeIds = [
            {
              id: 0,
              SchedulerBusySlotId: busySlotData.id,
              OfficeId: officeId,
              IsDeleted: false,
            },
          ];
        }

        const busySlotEmployeeView = (
          busySlotData.busySlotNotifiedUsers ||
          eventItem.busySlotNotifiedUsers ||
          []
        ).map((item) => ({
          userid: item.userId,
          OfficeId: item.officeId || officeId,
        }));

        const dateChangedResult = matchedBothDatesValues({
          eventDetail: busySlotData,
          repeatedEndDate: repeatedEndDate,
          requestedDate: originalRequestedDate,
          eventDate: eventDate.toDate(),
        });

        const repeatedOptionChanged = matchedBothPatternValues({
          repeatedType: repeatedType,
          repeatedTypeFrequency: busySlotData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: busySlotData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            busySlotData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: busySlotData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            busySlotData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            busySlotData.customRepeatPattern?.monthsOfYear?.map(
              (m) => m?.month,
            ) || [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: busySlotData,
        });

        const anyChangedForDate =
          dateChangedResult.startDateChanged ||
          dateChangedResult.endDateChanged ||
          repeatedOptionChanged;

        const neverCheck =
          repeatedType === addEditEventsConst.repeatedOptionsValues.never;
        if (!neverCheck && busySlotData.repeatedEndDate) {
          const newDateStr = moment(eventDate).format("YYYY-MM-DD");
          const repeatedEndDateStr = moment(
            busySlotData.repeatedEndDate,
          ).format("YYYY-MM-DD");
          if (
            !moment(newDateStr).isBefore(repeatedEndDateStr) &&
            newDateStr !== repeatedEndDateStr
          ) {
            toast.error(t("form.errors.endGreater"));
            return;
          }
        }

        const UpdateFlags = {
          isTitleEdit: false,
          isStartTimeEdit: getTimeCheck(
            busySlotData.startTime,
            formatBusySlotTimeValue({
              eventDateMoment: eventDate,
              isAllDayBusy,
              timeValue: startTime,
              startOrEnd: "startOf",
            }),
          ),
          isEndTimeEdit: getTimeCheck(
            busySlotData.endTime,
            formatBusySlotTimeValue({
              eventDateMoment: eventDate,
              isAllDayBusy,
              timeValue: endTime,
              startOrEnd: "endOf",
            }),
          ),
          IsAllDayBusyEdit: busySlotData.isAllDayBusy !== isAllDayBusy,
          isLocationEdit: false,
          IsDescriptionEdit: false,
          IsOfficeEdit: false,
          IsNotifiedUserEdit: false,
          IsNotifiedStaffListEdit: false,
          IsRepeatTypeChange: repeatedOptionChanged,
          IsBusySlotDateChange: dateChangedResult.startDateChanged,
          IsRepeatedEndDateChange: dateChangedResult.endDateChanged,
          OfficeIds: [],
          BusySlotEmployeeView: [],
        };

        const verified = {
          UpdateFlags,
          changedCheck: Object.values(UpdateFlags || {})?.some((value) =>
            typeof value === "boolean" ? value : value?.length > 0,
          ),
        };

        if (
          !neverCheck &&
          !editType &&
          (verified.changedCheck || anyChangedForDate)
        ) {
          setPendingDragUpdate({
            busySlotData,
            newDateTime,
            anyChangedForDate,
            isBusySlot: true,
            UpdateFlags,
            officeDetails,
            officeIds,
            busySlotEmployeeView,
            isAllDayBusy,
            repeatedType,
            repeatedEndDate,
            originalRequestedDate,
            eventItem,
          });
          setEditModalConfirmation(true);
          return;
        }

        let finalEditType = editType;
        if (!finalEditType) {
          if (neverCheck) {
            finalEditType = 3;
          } else {
            finalEditType = 3;
          }
        }

        await performBusySlotUpdate(
          busySlotData,
          newDateTime,
          finalEditType,
          officeDetails,
          officeIds,
          busySlotEmployeeView,
          isAllDayBusy,
          repeatedType,
          repeatedEndDate,
          originalRequestedDate,
          UpdateFlags,
        );
      } catch (error) {
        toast.error(error.message || t("scheduler.updateFailed"));
        throw error;
      }
    },
    [t, queryClient, getTimeCheck, performBusySlotUpdate],
  );

  // Handle drag start
  const handleDragStart = useCallback(
    (e, eventItem, fullEvent) => {
      if (!canDragEvent(eventItem)) {
        e.preventDefault();
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      let originalDate = null;
      if (eventItem.date) {
        originalDate = moment(eventItem.date).startOf("day");
      } else if (eventItem.startTime) {
        originalDate = moment(eventItem.startTime).startOf("day");
      }

      setDraggedEvent({
        eventItem,
        fullEvent,
        originalDate: originalDate ? originalDate.format("YYYY-MM-DD") : null,
      });
      setIsDragging(true);

      const dragImage = e.currentTarget.cloneNode(true);
      dragImage.style.opacity = "0";
      dragImage.style.position = "absolute";
      dragImage.style.top = "-1000px";
      document.body.appendChild(dragImage);
      e.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
      setTimeout(() => document.body.removeChild(dragImage), 0);
    },
    [canDragEvent],
  );

  // Check if event is all-day
  const isAllDayEvent = useCallback((eventItem) => {
    return (
      eventItem.isAllDayEvent ||
      eventItem.isAllDayBusy ||
      !eventItem.startTime ||
      !eventItem.endTime
    );
  }, []);

  // Handle drag over
  const handleDragOver = useCallback(
    (e, hourIndex, isAllDayRow = false) => {
      if (!draggedEvent) {
        e.preventDefault();
        return;
      }

      const { eventItem } = draggedEvent;
      const isDraggedAllDay = isAllDayEvent(eventItem);

      if (isDraggedAllDay && !isAllDayRow) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "none";
        setDragPreviewPosition(null);
        return;
      }
      if (!isDraggedAllDay && isAllDayRow) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "none";
        setDragPreviewPosition(null);
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      if (!isAllDayRow && !isDraggedAllDay && hourIndex !== undefined) {
        const cellElement = e.currentTarget;
        const cellRect = cellElement.getBoundingClientRect();
        const relativeY = e.clientY - cellRect.top;
        const cellHeight = cellRect.height;
        const minuteHeight = cellHeight / 60;
        const minutesFromHourStart = relativeY / minuteHeight;

        const snappedMinutes = Math.round(minutesFromHourStart / 15) * 15;
        const clampedMinute = Math.max(0, Math.min(45, snappedMinutes));

        const originalStart = moment(eventItem.startTime);
        const originalEnd = moment(eventItem.endTime);
        const durationMinutes = originalEnd.diff(originalStart, "minutes");

        const topPercent = (clampedMinute / 60) * 100;
        const heightPercent = (durationMinutes / 60) * 100;

        setDragPreviewPosition({
          hourIndex,
          topPercent,
          heightPercent,
          clampedMinute,
        });
      } else if (isAllDayRow && isDraggedAllDay) {
        setDragPreviewPosition({
          isAllDay: true,
        });
      }
    },
    [draggedEvent, isAllDayEvent],
  );

  // Handle drop
  const handleDrop = useCallback(
    async (e, hourIndex, isAllDayRow = false) => {
      e.preventDefault();
      if (!draggedEvent || !draggedEvent.eventItem) {
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      const { eventItem } = draggedEvent;
      if (!eventItem) {
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      const isDraggedAllDay = isAllDayEvent(eventItem);

      if (isDraggedAllDay && !isAllDayRow) {
        toast.error(t("scheduler.cannotMoveAllDayToTimeBased"));
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }
      if (!isDraggedAllDay && isAllDayRow) {
        toast.error(t("scheduler.cannotMoveTimeBasedToAllDay"));
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      if (isAllDayRow && isDraggedAllDay) {
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      if (!eventItem || !eventItem.id) {
        toast.error("Invalid event data");
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      const cellElement = e.currentTarget;
      const newDateTime = calculateNewDateTime(
        eventItem,
        e.clientY,
        cellElement,
        hourIndex,
      );

      if (newDateTime && newDateTime.error === "timeRangeExceeded") {
        toast.error("You can't move the event outside the time range");
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      if (!newDateTime) {
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
        return;
      }

      // Check if dropped on same time - if so, don't call API
      if (eventItem.startTime && newDateTime.newStartTime) {
        const originalStartTime = moment(eventItem.startTime);
        const newStartTime = moment(newDateTime.newStartTime);
        if (
          originalStartTime.hour() === newStartTime.hour() &&
          originalStartTime.minute() === newStartTime.minute()
        ) {
          setIsDragging(false);
          setDraggedEvent(null);
          setDragPreviewPosition(null);
          return;
        }
      }

      setIsDragDropLoading(true);
      try {
        if (
          eventItem.agendaType === constants.agendaType.BUSY_SLOT ||
          eventItem.agendaType === constants.agendaType.BLOCKED
        ) {
          await updateBusySlotAfterDrag(eventItem, newDateTime);
        } else {
          await updateEventAfterDrag(eventItem, newDateTime);
        }
      } catch (error) {
        // Error already shown in update functions
      } finally {
        setIsDragDropLoading(false);
        setIsDragging(false);
        setDraggedEvent(null);
        setDragPreviewPosition(null);
      }
    },
    [
      draggedEvent,
      isAllDayEvent,
      calculateNewDateTime,
      updateEventAfterDrag,
      updateBusySlotAfterDrag,
      t,
    ],
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (!isDragDropLoading) {
      setIsDragging(false);
      setDraggedEvent(null);
      setDragPreviewPosition(null);
    }
  }, [isDragDropLoading]);

  const renderEventContent = (event, dotColor) => {
    if (!event) return null;
    const eventItem = event.item || event;
    if (!eventItem) return null;
    return (
      <span className={styles.eventTextWrapper}>
        <span
          className={styles.eventDot}
          style={{ backgroundColor: dotColor || "#005f99" }}
        ></span>
        <span className={styles.eventTextContent}>
          {getEventDisplayText(eventItem)}
        </span>
      </span>
    );
  };

  // Get current date key
  const currentDateKey = useMemo(() => {
    if (!currentDate) return null;
    return moment(currentDate).format("ddd, MMM DD, YYYY");
  }, [currentDate]);

  // Check if date should be highlighted
  const highlightCheck = useMemo(() => {
    if (!dateMatchForHighlight || !currentDate) return false;
    return moment(dateMatchForHighlight)
      .startOf("day")
      .isSame(moment(currentDate).startOf("day"));
  }, [dateMatchForHighlight, currentDate]);

  const getHighlightEventKey = useCallback((item) => {
    if (!item?.id) return null;
    if (!item.startTime) return `allday-${item.id}`;
    return `${item.id}-${moment(item.startTime).valueOf()}`;
  }, []);

  const highlightTargetHourIndex = useMemo(() => {
    if (!dateMatchForHighlight || !highlightCheck) return null;
    return moment(dateMatchForHighlight).hour();
  }, [dateMatchForHighlight, highlightCheck]);

  // Match created item by showTime timestamp (firstOccurrenceDate from add flow)
  const highlightTargetEventKey = useMemo(() => {
    if (!dateMatchForHighlight || !highlightCheck) return null;

    const targetMoment = moment(dateMatchForHighlight);
    const targetMs = targetMoment.valueOf();
    const timedCandidates = timeEvents.map((e) => e.item).filter(Boolean);
    const allDayCandidates = allDayEvents.map((e) => e.item).filter(Boolean);

    const findContaining = (candidates) =>
      candidates.find((item) => {
        if (!item?.startTime) {
          return (
            item?.isAllDayBusy ||
            item?.isAllDayEvent ||
            targetMoment.isSame(moment(currentDate), "day")
          );
        }
        const start = moment(item.startTime);
        const end = item.endTime ? moment(item.endTime) : start.clone().add(1, "hour");
        return targetMoment.isBetween(start, end, undefined, "[]");
      });

    const timedMatch = findContaining(timedCandidates);
    if (timedMatch) return getHighlightEventKey(timedMatch);

    const allDayMatch = findContaining(allDayCandidates);
    if (allDayMatch) return getHighlightEventKey(allDayMatch);

    if (!timedCandidates.length) return null;

    let closest = null;
    let closestDiff = Infinity;
    timedCandidates.forEach((item) => {
      const startMs = moment(item.startTime).valueOf();
      const diff = Math.abs(startMs - targetMs);
      if (diff < closestDiff) {
        closestDiff = diff;
        closest = item;
      }
    });

    return closest ? getHighlightEventKey(closest) : null;
  }, [
    dateMatchForHighlight,
    highlightCheck,
    timeEvents,
    allDayEvents,
    currentDate,
    getHighlightEventKey,
  ]);

  const scrollToHighlightTarget = useCallback(() => {
    const target =
      highlightItemRef.current ||
      highlightHourRef.current ||
      highlightRef.current;

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      return true;
    }

    if (
      calendarWrapperRef.current &&
      highlightTargetHourIndex !== null &&
      highlightTargetHourIndex >= 0
    ) {
      const hourRows =
        calendarWrapperRef.current.querySelectorAll("tbody tr");
      const targetRow = hourRows[highlightTargetHourIndex];
      if (targetRow) {
        targetRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
        return true;
      }
    }

    return false;
  }, [highlightTargetHourIndex]);

  // Scroll to created busy slot time row / event; retry until agenda renders
  useEffect(() => {
    if (!search || !highlightCheck || isLoading || isFreeStotLoading) return;

    let scrollRetryId;
    let attempts = 0;
    const maxAttempts = 12;

    const tryScroll = () => {
      const didScroll = scrollToHighlightTarget();
      if (!didScroll && attempts < maxAttempts) {
        attempts += 1;
        scrollRetryId = setTimeout(tryScroll, 250);
      }
    };

    const scrollTimeout = setTimeout(tryScroll, 400);

    const timeOut = setTimeout(() => {
      const queryParams = new URLSearchParams(search);
      if (queryParams.has(showTime)) {
        queryParams.delete(showTime);
        history.replace({
          search: queryParams.toString(),
        });
      }
    }, 3000);

    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(scrollRetryId);
      clearTimeout(timeOut);
    };
  }, [
    search,
    highlightCheck,
    highlightTargetEventKey,
    highlightTargetHourIndex,
    isLoading,
    isFreeStotLoading,
    timeEvents,
    allDayEvents,
    history,
    scrollToHighlightTarget,
  ]);

  if (isLoading || isFreeStotLoading) {
    return <Loader />;
  }

  if (
    !agendaFilter?.length &&
    !isLoading &&
    agenda !== undefined &&
    agenda !== null
  ) {
    return <Empty Message={t("scheduler.noEventsFound")} />;
  }

  const holidayList = dailyAgendaData?.holidayList || [];
  const filteredAllDayEvents = filterEventsBySearch(
    allDayEvents,
    dailyAgendaData?.officeSchedulerList?.[0]?.user,
    searchValue,
  );
  const filteredTimeEvents = filterEventsBySearch(
    timeEvents,
    dailyAgendaData?.officeSchedulerList?.[0]?.user,
    searchValue,
  );

  return (
    <>
      {isDragDropLoading && <Loader />}
      <div ref={calendarWrapperRef} className={styles.calendarWrapper}>
        <table className={styles.calendarTable}>
          <thead>
            {/* Row 1: Date header */}
            <tr>
              <th className={styles.timeHeader}>
                <div className={styles.empty}></div>
              </th>
              <th
                ref={highlightCheck ? highlightRef : undefined}
                className={`${styles.dayHeader} ${schedulerStyles.custom_th} ${
                  schedulerStyles.custom_th_dots
                } ${highlightCheck ? "blinking-background" : ""}`}
              >
                <div className={schedulerStyles.weekly_agenda_header}>
                  {currentDateKey}
                  {currentDate &&
                    !isPastDate(
                      moment(currentDate).format("YYYY-MM-DDTHH:mm:ss"),
                    ) && (
                      <UncontrolledDropdown
                        className={schedulerStyles.ellipsis_position_dotss}
                      >
                        <DropdownToggle
                          caret={false}
                          tag="div"
                          className="cursor-pointer"
                        >
                          <div
                            className={
                              schedulerStyles.ellipsis_icon +
                              " " +
                              "ellipsis-icon"
                            }
                          >
                            <ThreeDots />
                          </div>
                        </DropdownToggle>

                        <DropdownMenu
                          end
                          className={schedulerStyles["office-dropdown-menu"]}
                        >
                          {isStaff && (
                            <DropdownItem
                              className={
                                schedulerStyles["office-dropdown-item"]
                              }
                              onClick={() => {
                                setShowOfficeSelectModal(true);
                                setSelectedDateForLeave(
                                  moment(currentDate).toDate(),
                                );
                              }}
                            >
                              {t("staffLeaves.applyLeave")}
                            </DropdownItem>
                          )}
                          <DropdownItem
                            className={schedulerStyles["office-dropdown-item"]}
                            onClick={() => {
                              history.push({
                                pathname:
                                  constants.routes.scheduler.addBusySlot,
                                state: {
                                  date: moment(currentDate).toDate(),
                                },
                              });
                            }}
                          >
                            {t("accountOwner.addBusyslots")}
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}
                </div>
              </th>
            </tr>

            {/* Row 2: Holiday indicator */}
            {holidayList.length > 0 && (
              <tr>
                <th className={styles.timeHeader}>
                  <div className={styles.empty_holi}></div>
                </th>
                <th className={styles.holiday_header}>
                  <div className={styles.holiday_container}>
                    <img src={HolidayImage} alt="holiday" />
                    <p className={styles.holiday_text}>
                      {holidayList[0]?.title || "HOLIDAY"}
                    </p>
                  </div>
                </th>
              </tr>
            )}

            {/* All-day events row */}
            <tr>
              <th
                className={`${styles.timeHeader} ${styles.all_day_event_header}`}
              >
                <p className={styles.all_day_text}>all-day</p>
              </th>
              <td
                className={`${styles.eventCell} ${
                  holidayList.length > 0 ? styles.light_red_bg : ""
                } ${highlightCheck ? "blinking-background" : ""}`}
                onDragOver={(e) => handleDragOver(e, 0, true)}
                onDrop={(e) => handleDrop(e, 0, true)}
              >
                {filteredAllDayEvents.map((event, eIndex) => {
                  if (!event || !event.user || !event.item) return null;
                  const eventItem = event.item;
                  const isHighlightTarget =
                    highlightTargetEventKey === getHighlightEventKey(eventItem);
                  const createdByMe = eventItem.createdById === currentUserId;
                  const isInvite = !!eventItem.eventEmployeeStatus;
                  const canDrag = canDragEvent(eventItem);
                  const isDragged =
                    isDragging && draggedEvent?.eventItem?.id === eventItem.id;

                  const shouldRedirect =
                    eventItem.agendaType === constants.agendaType.EVENT ||
                    eventItem.agendaType === constants.agendaType.BUSY_SLOT ||
                    eventItem.agendaType === constants.agendaType.LEAVE;
                  const shouldShowModal =
                    eventItem.agendaType === constants.agendaType.BLOCKED ||
                    eventItem.agendaType === constants.agendaType.APPOINTMENT;

                  return (
                    <React.Fragment key={eIndex}>
                      <div
                        ref={isHighlightTarget ? highlightItemRef : undefined}
                        className={styles.mySchedule_dayView_eventBox}
                        draggable={canDrag}
                        onDragStart={(e) =>
                          handleDragStart(e, eventItem, event)
                        }
                        onDragEnd={handleDragEnd}
                        style={{
                          backgroundColor: event.bgColor,
                          cursor: canDrag
                            ? "grab"
                            : shouldRedirect || shouldShowModal
                              ? "pointer"
                              : "default",
                          opacity: isDragged ? 0 : 1,
                          border: "1px solid rgba(0, 0, 0, 0.2)",
                        }}
                        onClick={(e) => {
                          if (isDragging) {
                            e.stopPropagation();
                            return;
                          }
                          const requestedDate = dailyAgendaData?.requestedDate;

                          if (shouldRedirect) {
                            goToDetails(
                              eventItem.id,
                              eventItem.agendaType,
                              createdByMe,
                              requestedDate,
                              isInvite,
                              eventItem,
                            );
                          } else if (shouldShowModal) {
                            setIsModal({
                              open: true,
                              type: eventItem.agendaType,
                              officeId: eventItem?.officeId || null,
                            });
                          }
                        }}
                      >
                        {renderEventContent(event, event.dotColor)}
                      </div>
                    </React.Fragment>
                  );
                })}
                {/* Drag preview for all-day events */}
                {isDragging &&
                  dragPreviewPosition &&
                  dragPreviewPosition.isAllDay &&
                  draggedEvent &&
                  draggedEvent.eventItem && (
                    <div
                      style={{
                        backgroundColor: getEventColor(draggedEvent.eventItem),
                        border: `2px dashed ${getEventBorderColor(
                          draggedEvent.eventItem,
                        )}`,
                        borderRadius: 4,
                        opacity: 0.6,
                        padding: "4px 8px",
                        marginTop: "4px",
                        pointerEvents: "none",
                        fontSize: "12px",
                      }}
                    >
                      {draggedEvent.fullEvent
                        ? renderEventContent(
                            draggedEvent.fullEvent,
                            draggedEvent.fullEvent?.dotColor || "#005f99",
                          )
                        : renderEventContent(
                            { item: draggedEvent.eventItem },
                            "#005f99",
                          )}
                    </div>
                  )}
              </td>
            </tr>
          </thead>
          <tbody>
            {/* Time-based events rows */}
            {hours.map((hour, hIndex) => {
              const hourStartMinutes = hIndex * 60;
              const hourEndMinutes = (hIndex + 1) * 60;

              const overlappingEvents = filteredTimeEvents.filter((event) => {
                if (
                  !event ||
                  !event.item ||
                  !event.item.startTime ||
                  !event.item.endTime
                ) {
                  return false;
                }
                const startMinutes = getTimeInMinutes(event.item.startTime);
                const endMinutes = getTimeInMinutes(event.item.endTime);
                return (
                  startMinutes < hourEndMinutes && endMinutes > hourStartMinutes
                );
              });

              const groupedEvents = groupEventsByStartTime(
                overlappingEvents.map((e) => e.item).filter(Boolean),
              );

              return (
                <tr
                  key={hIndex}
                  ref={
                    highlightCheck && highlightTargetHourIndex === hIndex
                      ? highlightHourRef
                      : undefined
                  }
                >
                  <td className={styles.timeColumn}>
                    <div className={styles.date_container}>{hour.label}</div>
                  </td>
                  <td
                    className={`${styles.eventCell} ${
                      holidayList.length > 0 ? styles.light_red_bg : ""
                    } ${highlightCheck ? "blinking-background" : ""}`}
                    style={{ position: "relative", height: "60px" }}
                    onDragOver={(e) => handleDragOver(e, hIndex, false)}
                    onDrop={(e) => handleDrop(e, hIndex, false)}
                  >
                    {Object.entries(groupedEvents).map(
                      ([startMinutesStr, group]) => {
                        const overlapPositions =
                          calculateOverlapPositions(group);

                        return overlapPositions.map(
                          (
                            { event: eventItem, widthFactor, leftOffsetFactor },
                            index,
                          ) => {
                            let finalEvent = null;

                            for (const fullEvent of overlappingEvents) {
                              if (!fullEvent || !fullEvent.item) continue;

                              if (
                                fullEvent.item.id === eventItem.id &&
                                fullEvent.item.startTime ===
                                  eventItem.startTime &&
                                fullEvent.item.endTime === eventItem.endTime
                              ) {
                                finalEvent = fullEvent;
                                break;
                              }
                            }

                            if (!finalEvent) {
                              for (const fullEvent of overlappingEvents) {
                                if (!fullEvent || !fullEvent.item) continue;

                                if (
                                  fullEvent.item.id === eventItem.id &&
                                  fullEvent.item.startTime ===
                                    eventItem.startTime
                                ) {
                                  finalEvent = fullEvent;
                                  break;
                                }
                              }
                            }

                            if (!finalEvent || !finalEvent.user) return null;

                            const startMinutes = getTimeInMinutes(
                              eventItem.startTime,
                            );
                            const endMinutes = getTimeInMinutes(
                              eventItem.endTime,
                            );

                            const eventStartInHour = Math.max(
                              startMinutes,
                              hourStartMinutes,
                            );
                            const eventEndInHour = Math.min(
                              endMinutes,
                              hourEndMinutes,
                            );

                            const top =
                              ((eventStartInHour - hourStartMinutes) / 60) *
                              100;
                            let height =
                              ((eventEndInHour - eventStartInHour) / 60) * 100;

                            const HOUR_CELL_HEIGHT_PX = 60;
                            const MIN_EVENT_HEIGHT_PX = 8;
                            let eventHeightPx =
                              (height / 100) * HOUR_CELL_HEIGHT_PX;
                            let topPx = (top / 100) * HOUR_CELL_HEIGHT_PX;

                            if (eventHeightPx < MIN_EVENT_HEIGHT_PX) {
                              eventHeightPx = MIN_EVENT_HEIGHT_PX;
                              if (eventHeightPx + topPx > HOUR_CELL_HEIGHT_PX) {
                                eventHeightPx = HOUR_CELL_HEIGHT_PX - topPx;
                              }
                            }
                            const heightPercent =
                              (eventHeightPx / HOUR_CELL_HEIGHT_PX) * 100;
                            const topPercent =
                              (topPx / HOUR_CELL_HEIGHT_PX) * 100;

                            const eventStartHour = Math.floor(
                              startMinutes / 60,
                            );
                            const isFirstHourOfEvent =
                              eventStartHour === hIndex;
                            const isHighlightTarget =
                              highlightTargetEventKey ===
                                getHighlightEventKey(eventItem) &&
                              isFirstHourOfEvent;

                            const continuesNextHour =
                              endMinutes > hourEndMinutes;
                            const continuesFromPrevHour =
                              startMinutes < hourStartMinutes;

                            const eventColor = getEventColor(eventItem);

                            const createdByMe =
                              eventItem.createdById === currentUserId;
                            const isInvite = !!eventItem.eventEmployeeStatus;
                            const canDrag = canDragEvent(eventItem);
                            const isDragged =
                              isDragging &&
                              draggedEvent?.eventItem?.id === eventItem.id;

                            const shouldRedirect =
                              eventItem.agendaType ===
                                constants.agendaType.EVENT ||
                              eventItem.agendaType ===
                                constants.agendaType.BUSY_SLOT ||
                              eventItem.agendaType ===
                                constants.agendaType.LEAVE;
                            const shouldShowModal =
                              eventItem.agendaType ===
                                constants.agendaType.BLOCKED ||
                              eventItem.agendaType ===
                                constants.agendaType.APPOINTMENT;

                            return (
                              <React.Fragment key={`${eventItem.id}-${index}`}>
                                <div
                                  ref={
                                    isHighlightTarget
                                      ? highlightItemRef
                                      : undefined
                                  }
                                  draggable={canDrag}
                                  onDragStart={(e) =>
                                    handleDragStart(e, eventItem, finalEvent)
                                  }
                                  onDragEnd={handleDragEnd}
                                  style={{
                                    position: "absolute",
                                    top: `${topPercent}%`,
                                    left: `${leftOffsetFactor * 100}%`,
                                    width: `${widthFactor * 100 - 1}%`,
                                    height: `${heightPercent}%`,
                                    backgroundColor: eventColor,
                                    borderRadius:
                                      continuesFromPrevHour || continuesNextHour
                                        ? 0
                                        : 6,
                                    borderTopLeftRadius: continuesFromPrevHour
                                      ? 0
                                      : 6,
                                    borderTopRightRadius: continuesFromPrevHour
                                      ? 0
                                      : 6,
                                    borderBottomLeftRadius: continuesNextHour
                                      ? 0
                                      : 6,
                                    borderBottomRightRadius: continuesNextHour
                                      ? 0
                                      : 6,
                                    zIndex: isDragged ? 1000 : 2,
                                    overflow: "hidden",
                                    cursor: canDrag
                                      ? "grab"
                                      : shouldRedirect || shouldShowModal
                                        ? "pointer"
                                        : "default",
                                    padding: "2px 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    opacity: isDragged ? 0 : 1,
                                    borderLeft: "1px solid rgba(0, 0, 0, 0.2)",
                                    borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                                    borderTop: continuesFromPrevHour
                                      ? "none"
                                      : "1px solid rgba(0, 0, 0, 0.2)",
                                    borderBottom: continuesNextHour
                                      ? "none"
                                      : "1px solid rgba(0, 0, 0, 0.2)",
                                  }}
                                  onClick={(e) => {
                                    if (isDragging) {
                                      e.stopPropagation();
                                      return;
                                    }
                                    const requestedDate =
                                      dailyAgendaData?.requestedDate;

                                    if (shouldRedirect) {
                                      goToDetails(
                                        eventItem.id,
                                        eventItem.agendaType,
                                        createdByMe,
                                        requestedDate,
                                        isInvite,
                                        eventItem,
                                      );
                                    } else if (shouldShowModal) {
                                      setIsModal({
                                        open: true,
                                        type: eventItem.agendaType,
                                        officeId: eventItem?.officeId || null,
                                      });
                                    }
                                  }}
                                >
                                  {isFirstHourOfEvent && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "#2D3245",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        width: "100%",
                                      }}
                                    >
                                      {renderEventContent(
                                        finalEvent,
                                        finalEvent.dotColor,
                                      )}
                                    </div>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          },
                        );
                      },
                    )}
                    {/* Drag preview for time-based events */}
                    {isDragging &&
                      dragPreviewPosition &&
                      !dragPreviewPosition.isAllDay &&
                      dragPreviewPosition.hourIndex === hIndex &&
                      draggedEvent &&
                      draggedEvent.eventItem && (
                        <div
                          style={{
                            position: "absolute",
                            top: `${dragPreviewPosition.topPercent}%`,
                            left: 0,
                            width: "100%",
                            height: `${dragPreviewPosition.heightPercent}%`,
                            backgroundColor: getEventColor(
                              draggedEvent.eventItem,
                            ),
                            border: `2px dashed ${getEventBorderColor(
                              draggedEvent.eventItem,
                            )}`,
                            borderRadius: 6,
                            opacity: 0.6,
                            zIndex: 1000,
                            pointerEvents: "none",
                            display: "flex",
                            alignItems: "center",
                            padding: "2px 4px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              color: "#2D3245",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                            }}
                          >
                            {draggedEvent.fullEvent
                              ? renderEventContent(
                                  draggedEvent.fullEvent,
                                  draggedEvent.fullEvent?.dotColor || "#005f99",
                                )
                              : renderEventContent(
                                  { item: draggedEvent.eventItem },
                                  "#005f99",
                                )}
                          </div>
                        </div>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <MoveToApp
          isModal={isModal?.open}
          openModal={setIsModal}
          type={isModal?.type}
          officeId={isModal?.officeId || null}
        />
      </div>
      {editModalConfirmation && pendingDragUpdate && (
        <EditOptionsModal
          isOpen={editModalConfirmation}
          closeStepOneModal={() => {
            setEditModalConfirmation(false);
            setPendingDragUpdate(null);
            setIsDragging(false);
            setDraggedEvent(null);
            setDragPreviewPosition(null);
          }}
          loader={isDragDropLoading}
          eventKey={
            pendingDragUpdate.isBusySlot
              ? {
                  eventOrShift: t("accountOwner.busySlot").toLowerCase(),
                }
              : {
                  eventOrShift: t(
                    `accountOwner.${
                      pendingDragUpdate.eventData?.isShift ? "shift" : "event"
                    }Tab`,
                  ).toLowerCase(),
                }
          }
          eventOrShiftTab={
            pendingDragUpdate.isBusySlot
              ? false
              : pendingDragUpdate.eventData?.isShift || false
          }
          busySlots={pendingDragUpdate.isBusySlot || false}
          repeatedOptionChanged={pendingDragUpdate.anyChangedForDate || false}
          handleConfirm={async (value) => {
            try {
              setIsDragDropLoading(true);
              if (pendingDragUpdate.isBusySlot) {
                await performBusySlotUpdate(
                  pendingDragUpdate.busySlotData,
                  pendingDragUpdate.newDateTime,
                  value,
                  pendingDragUpdate.officeDetails,
                  pendingDragUpdate.officeIds,
                  pendingDragUpdate.busySlotEmployeeView,
                  pendingDragUpdate.isAllDayBusy,
                  pendingDragUpdate.repeatedType,
                  pendingDragUpdate.repeatedEndDate,
                  pendingDragUpdate.originalRequestedDate,
                  pendingDragUpdate.UpdateFlags,
                );
              } else {
                await performEventUpdate(
                  pendingDragUpdate.eventData,
                  pendingDragUpdate.newDateTime,
                  value,
                  pendingDragUpdate.originalRequestedDate,
                );
              }
            } catch (error) {
              // Error already shown in perform functions
            } finally {
              setIsDragDropLoading(false);
              setIsDragging(false);
              setDraggedEvent(null);
              setDragPreviewPosition(null);
              setEditModalConfirmation(false);
              setPendingDragUpdate(null);
            }
          }}
        />
      )}
      {showOfficeSelectModal && (
        <SelectOfficeModal
          selectedOwnerId={selectedOwnerId}
          showOfficeSelectModal={showOfficeSelectModal}
          setShowOfficeSelectModal={setShowOfficeSelectModal}
          SelectedDateForLeave={SelectedDateForLeave}
        />
      )}
    </>
  );
}

export default withTranslation()(DailyListView);
